import { useState, useContext, useEffect, useRef, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
// @mui
import { styled } from '@mui/material/styles';
import { Link, Container, Typography, Stack, Button, Alert, IconButton, AlertTitle, Box, LinearProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
// hooks
import useResponsive from '../hooks/useResponsive';
// components
import Logo from '../components/logo';
// sections
import { LoginForm, SignInForm, ChangePasswordForm, AccountConfirmationForm } from '../sections/auth/login';

import { AccountContext } from '../account';
import { SUPPORT_WHATSAPP_URL } from '../utils/constants';

const REGISTER_SUCCESS_REDIRECT_SECONDS = 4;

// ----------------------------------------------------------------------

const StyledRoot = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    display: 'flex',
  },
}));

const StyledSection = styled('div')(({ theme }) => ({
  width: '100%',
  maxWidth: 480,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  boxShadow: theme.customShadows.card,
  backgroundColor: theme.palette.background.default,
}));

const StyledContent = styled('div')(({ theme }) => ({
  maxWidth: 480,
  margin: 'auto',
  minHeight: '100vh',
  display: 'flex',
  justifyContent: 'center',
  flexDirection: 'column',
  padding: theme.spacing(12, 0),
}));

// Erros da Cognito com mensagem própria; o resto cai na mensagem do fluxo (antes era engolido e a tela não dizia nada)
const MENSAGENS_COGNITO = {
  LimitExceededException: 'Limite de tentativas excedido, tente novamente mais tarde.',
  TooManyRequestsException: 'Muitas tentativas em pouco tempo, tente novamente mais tarde.',
  CodeDeliveryFailureException: 'Não conseguimos enviar o código. Tente novamente em alguns minutos.',
  CodeMismatchException: 'Código inválido. Confira o código recebido.',
  ExpiredCodeException: 'Código expirado. Peça um novo código.',
  InvalidPasswordException: 'A nova senha não atende aos requisitos de segurança.',
};

const ErrorAlert = (props) => {
  return (
    <Alert severity="error" 
    action={
      <IconButton
        aria-label="close"
        color="inherit"
        size="small"
        onClick={() => {
          props.setOpen(false);
        }}
      >
        <CloseIcon fontSize="inherit" />
      </IconButton>
    }
      sx={{ mb: 2 }}
    >
    <AlertTitle>Error</AlertTitle>
    {props.message}
    </Alert>
  )
}

function RegisterSuccessRedirect({ onComplete }) {
  const [timeRemaining, setTimeRemaining] = useState(REGISTER_SUCCESS_REDIRECT_SECONDS);
  const timerRef = useRef(null);

  useEffect(() => {
    timerRef.current = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          if (timerRef.current) {
            clearInterval(timerRef.current);
          }
          onComplete();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [onComplete]);

  const handleRedirectNow = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    onComplete();
  };

  return (
    <Container maxWidth="sm">
      <StyledContent>
        <Stack spacing={4} alignItems="center" sx={{ textAlign: 'center' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: { xs: 80, sm: 100 },
              height: { xs: 80, sm: 100 },
              borderRadius: '50%',
              bgcolor: 'success.lighter',
            }}
          >
            <CheckCircleIcon color="success" sx={{ fontSize: { xs: 56, sm: 72 } }} />
          </Box>
          <Box>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
              Cadastro realizado com sucesso!
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Agora você pode entrar com seu CPF e senha.
            </Typography>
          </Box>
          <Box sx={{ width: '100%', maxWidth: 400 }}>
            <Stack spacing={2} alignItems="center">
              <Typography variant="body2" color="text.secondary">
                Redirecionando em {timeRemaining} segundo{timeRemaining !== 1 ? 's' : ''}...
              </Typography>
              <LinearProgress
                variant="determinate"
                value={
                  ((REGISTER_SUCCESS_REDIRECT_SECONDS - timeRemaining) /
                    REGISTER_SUCCESS_REDIRECT_SECONDS) *
                  100
                }
                sx={{
                  width: '100%',
                  height: 6,
                  borderRadius: 3,
                  bgcolor: 'grey.200',
                  '& .MuiLinearProgress-bar': { borderRadius: 3 },
                }}
              />
              <Button variant="outlined" size="small" onClick={handleRedirectNow}>
                Ir agora
              </Button>
            </Stack>
          </Box>
        </Stack>
      </StyledContent>
    </Container>
  );
}

// ----------------------------------------------------------------------

export default function LoginPage() {

  const platformName = 'Chancela Fácil'

  const mdUp = useResponsive('up', 'md');

  // status = 'register', 'login', 'loged', 'resetPassord', 'accountConfirmation', 'registerSuccess'
  const [status, setStatus] = useState('login')
  const [userName, setUserName] = useState('')
  const [password, setPassword] = useState('')
  const [open, setOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const { authenticate, verifyAccount, forgotPassword, confirmForgotPassword } = useContext(AccountContext)

  const mostrarErro = (err, padrao) => {
    setErrorMessage(MENSAGENS_COGNITO[err?.name] || padrao)
    setOpen(true)
  }

  const handleRegisterSuccessComplete = useCallback(() => {
    setStatus('login');
  }, []);

  const HandleSuport = () => {
    const whatsappURL = `${SUPPORT_WHATSAPP_URL.replace('suporte', 'ajuda para acessar minha conta')}`;
    window.open(whatsappURL, '_blank');  
};

  const signUp = (email, password) => {
      authenticate(email, password)
      .then(data => {
        window.location.reload();
      })
      .catch(err => {
        
        if(err.name === 'UserNotConfirmedException'){
          setUserName(email)
          setPassword(password)
          setStatus('accountConfirmation')
        } else if(err.name === 'InvalidParameterException'){
          setOpen(true)
          setErrorMessage('Por favor, insira dados válidos.')
        } else if(err.name === 'NotAuthorizedException'){
          setOpen(true)
          setErrorMessage('Usuário ou senha incorretos.')
        } else {
          mostrarErro(err, 'Não foi possível entrar. Tente novamente ou fale com o suporte.')
        }
        
      }
      )
    
  };

  const tryForgotPassword = (documentNumber) => {
    forgotPassword(documentNumber)
    .then(data => {
      setOpen(false)
      setUserName(documentNumber)
      setStatus('resetPassord')
      }
    ).catch(err => mostrarErro(err, 'Não foi possível enviar o código para esta conta. Confira o CPF ou fale com o suporte.'))
  };
  
  const verify = async (OTP) => {

    const done = await verifyAccount(userName, OTP)

    if(done){
      await signUp(userName, password)
    } else {
      mostrarErro(null, 'Código inválido ou expirado.')
    }
    
  };

  const veryfyForgotPassword = async (OTP, newPassword) => {
    let done
    try {
      done = await confirmForgotPassword(userName, OTP, newPassword)
    } catch (err) {
      mostrarErro(err, 'Não foi possível trocar a senha. Tente novamente ou fale com o suporte.')
      return
    }

    if(done){
      await signUp(userName, newPassword)
    }
  };

  const HandleUserStatus = () => {

    switch(status) {
      case 'login':
        return (
          <Container maxWidth="sm">
          <StyledContent>
            <Typography variant="h4" gutterBottom>
              Entrar em {platformName}
            </Typography>

            <Typography variant="body2" sx={{ mb: 3 }}>
            Não tem uma conta? {''}
            <Link variant="subtitle2" sx={{ cursor: 'pointer' }} onClick={_ => setStatus('register')}>Cadastre-se</Link>
            </Typography>

            { open ? <ErrorAlert setOpen={setOpen} message={errorMessage} /> : <></> }

            <LoginForm signUp={signUp} setStatus={setStatus} tryForgotPassword={tryForgotPassword}/>

          </StyledContent>
        </Container>
        )
      case 'register':
        return (
          <Container maxWidth="sm">
          <StyledContent>
            <Typography variant="h4" gutterBottom>
              Registrar-se em {platformName}
            </Typography>

            <Typography variant="body2" sx={{ mb: 3 }}>
            Já tem uma conta? {''}
            <Link variant="subtitle2" sx={{ cursor: 'pointer' }} onClick={_ => setStatus('login')}>Entrar</Link>
            </Typography>

            { open ? <ErrorAlert setOpen={setOpen} message={errorMessage} /> : <></> }

            <SignInForm setStatus={setStatus} setOpen={setOpen} setErrorMessage={setErrorMessage}/>

          </StyledContent>
        </Container>
        )
      case 'registerSuccess':
        return <RegisterSuccessRedirect onComplete={handleRegisterSuccessComplete} />;
        case 'resetPassord':
          return (
            <Container maxWidth="sm">
            <StyledContent>
              <Typography variant="h4" gutterBottom>
                Não consegue acessar sua conta?
              </Typography>

              { open ? <ErrorAlert setOpen={setOpen} message={errorMessage} /> : <></> }
    
              <ChangePasswordForm veryfyForgotPassword={veryfyForgotPassword}/>
              {/* texto "Clique aqui para entrar em contato com nosso suporte", onde no "clique aqui" é um link para o whatsapp do suporte */}
              {/* <Typography variant="body2" sx={{ mb: 3 }}>
                <Link variant="subtitle2" onClick={_ => HandleSuport()}>Clique aqui</Link>
                {''} para entrar em contato com nosso suporte.
              </Typography> */}

              
            </StyledContent>
          </Container>
          )
          case 'accountConfirmation':
            return (
              <Container maxWidth="sm">
              <StyledContent>
                <Typography variant="h4" gutterBottom>
                  Confirmar conta
                </Typography>
                <Typography variant="body2" sx={{ mb: 3 }}>
                Enviamos um token de acesso para o seu email
                </Typography>

                { open ? <ErrorAlert setOpen={setOpen} message={errorMessage} /> : <></> }
      
                <AccountConfirmationForm verify={verify}/>
    
              </StyledContent>
            </Container>
            )
      default:
        return <></>
    }

  }

  return (
    <>
      <Helmet>
        <title>{status === 'registerSuccess' ? 'Cadastro concluído' : 'Login'}</title>
      </Helmet>

      <StyledRoot>
        <Logo
          sx={{
            width: 110,
            height: 22,
            position: 'fixed',
            top: { xs: 16, sm: 24, md: 40 },
            left: { xs: 16, sm: 24, md: 40 },
          }}
        />

        {mdUp && (
          <StyledSection>
            <Typography variant="h3" sx={{ px: 5, mt: 10, mb: 5 }}>
              Oi! Bem-vindo de volta.
            </Typography>
            <img src="/assets/illustrations/login.png" alt="login" />
          </StyledSection>
        )}

        {HandleUserStatus()}
      </StyledRoot>
    </>
  );
}
